import { Box, Card, CardBody, Container, Flex, Heading, HStack, Image, Spinner, Text, VStack } from '@chakra-ui/react';
import ContentLayout from '@layouts/ContentLayout';
import { useDispatch, useSelector } from 'react-redux';
import { getUserById } from '@store/actions/usersActions';
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import profileLogo from '@assets/icons/profile.svg';
import GenericCard from '@components/GenericCard';
import { CARD_TYPE } from '@constants';
import CopyableText from '@components/CopyableText';
import { Button, useDisclosure } from '@chakra-ui/react';
import ModalLayout from '@components/Modal/ModalLayout';
import DynamicInputModal from '@components/DynamicInputModal';
import { updateUserAddressV2 } from '@store/actions/usersActions';
import { getUserAddressFieldsConfig } from './utils';
import useAuthRole from '@hooks/useAuthRole';
import { ROLES } from '@constants';

const UserProfileDetails = () => {
    const { data: userDetails, isLoading: isLoadingUser } = useSelector(
        (state) => state.users.userDetails
    );
    const { hasAnyAuthority } = useAuthRole();
    const { userId } = useParams();
    const dispatch = useDispatch();
    const isUpdating = useSelector(state => state.users.isUpdatingUserAddress);
    const {
        isOpen: isAddressModalOpen,
        onOpen: onAddressModalOpen,
        onClose: onAddressModalClose
    } = useDisclosure();

    useEffect(() => {
        dispatch(getUserById(userId));
    }, [userId, dispatch]);

    const userBasicInfo = {
        name: userDetails?.name,
        number: userDetails?.number,
        userId: userDetails?.userId
    };

    // Prepare address fields for DynamicInputModal
    const addressFields = getUserAddressFieldsConfig(userDetails?.userAddress);

    // Handler for address update
    const handleAddressUpdate = async (formData) => {
        await dispatch(updateUserAddressV2({
            userId,
            username: userDetails?.name,
            address: formData,
            onSuccess: () => dispatch(getUserById(userId)),
        }));
    };

    if (isLoadingUser) {
        return (
            <ContentLayout>
                <Flex
                    justify="center"
                    align="center"
                    minH="50vh"
                >
                    <Spinner
                        size="xl"
                        color="colorPrimary"
                        thickness="4px"
                    />
                </Flex>
            </ContentLayout>
        );
    }

    return (
        <ContentLayout>
            <Container maxW="container.xl" py={8}>
                <Card mb={8} shadow="lg">
                    <CardBody>
                        <HStack spacing={6} align="center">
                            <Image
                                src={profileLogo}
                                alt="Profile Logo"
                                boxSize={{ base: '80px', md: '100px', lg: '120px' }}
                                borderRadius="md"
                                objectFit="cover"
                            />
                            <VStack align="start" spacing={2}>
                                <Heading
                                    color="colorPrimary"
                                    size="xl"
                                    fontWeight="bold"
                                >
                                    {userDetails?.name || 'User Profile'}
                                </Heading>
                                <Box
                                    display={'flex'}
                                    flexDirection={{ base: 'column', md: 'row' }}
                                    color="gray.600"
                                    fontSize="lg"
                                    fontWeight="medium"
                                >
                                    <Text mr={{ base: 0, md: 2 }} mb={{ base: 1, md: 0 }}>Referral Code:</Text>
                                    <CopyableText>
                                        {userDetails?.referralCode}
                                    </CopyableText>

                                </Box>
                            </VStack>
                        </HStack>
                    </CardBody>
                </Card>

                <VStack spacing={6} align="stretch">
                    <GenericCard cardData={userBasicInfo} cardType={CARD_TYPE.USER_DETAILS_BASIC} />
                    <GenericCard cardData={userDetails?.userProfile} cardType={CARD_TYPE.USER_DETAILS_PROFILE} />
                    <GenericCard
                        cardData={userDetails?.userAddress}
                        cardType={CARD_TYPE.USER_DETAILS_ADDRESS}
                        ActionButtons={
                            hasAnyAuthority(ROLES.ADMIN_ROLE, ROLES.MANAGER_ROLE) && (
                                <Button colorScheme="brand" bg="brand.700" onClick={onAddressModalOpen} size="sm">
                                    Update Address
                                </Button>
                            )
                        }
                    />
                </VStack>
                {/* Update Address Button and Modal at the bottom */}
                {/* Removed the separate Update Address button here */}
                <ModalLayout isOpen={isAddressModalOpen} onClose={onAddressModalClose} size="sm">
                    <DynamicInputModal
                        title="Update Address"
                        fields={addressFields}
                        isLoading={isUpdating}
                        onClose={onAddressModalClose}
                        onSubmit={handleAddressUpdate}
                    />
                </ModalLayout>
            </Container>
        </ContentLayout>
    );
};

export default UserProfileDetails;